/* eslint-disable max-lines-per-function */
import ParameterValidator from '@mvps-genesis/parameter-validator';

/*
 *--------------------------------------------------------------------------
 * Shipping Estimator
 *--------------------------------------------------------------------------
 *
 * Use this class to retrieve estimated shipping rates data from the
 * Miva SERT page and interact with the estimate shipping form.
 *
 */
export default class ShippingEstimator {

	/**
	 * Parameter Validator object
	 *
	 * @var	@mvps-genesis/parameter-validator	ParameterValidator
	 */
	#validator;

	/**
	 * API settings object
	 *
	 * @var		Object
	 * @prop	String	fieldName	The field name to enable the SERT page API
	 * @prop	String	url			The SERT page URL
	 */
	api;

	/**
	 * The form element
	 *
	 * {HTMLElement}
	 */
	formElement;

	/**
	 * Shipping estimate form settings object
	 *
	 * @var		Object
	 * @prop	Object	fields				The shipping estimate form fields object
	 * @prop	Element	fields.container	The shipping estimate form fields container
	 * @prop	String	fields.controlClass	The shipping estimate form fields display control class
	 * @prop	Object	submit				The shipping estimate form submit object
	 * @prop	Element	submit.element		The shipping estimate form submit element
	 * @prop	Element	submit.originalText	The shipping estimate form submit element original text value
	 * @prop	Element	submit.text			The shipping estimate form submit element text value
	 */
	form;

	/**
	 * The estimated shipping results object
	 *
	 * @var		Object
	 * @prop	Element	container					The estimated shipping results container element
	 * @prop	Object	data						The SERT API response object
	 * @prop	Object	data.rates					The SERT API response ship estimate rates data object
	 * @prop	String	data.template				The SERT API response template
	 * @prop	Boolean	data.success				The SERT API response success flag
	 * @prop	Object	recalculate					The estimated shipping results recalculate object
	 * @prop	Element	recalculate.element			The estimated shipping results recalculate element
	 * @prop	String	recalculate.triggerClass	The estimated shipping results recalculate trigger class
	 */
	results;

	/**
	 * Create a new ShippingEstimator object
	 *
	 * @param  {HTMLElement|String}	 formElement                       The shipping estimate form Element object.
	 * @param  {HTMLElement|String}	 options.formFieldsContainer       The form fields container selector. Defaults to '.js-shipping-estimate-fields'.
	 * @param  {String}              options.formFieldsControlClass    The class that controls the form fields display. Defaults to 'u-hidden'.
	 * @param  {HTMLElement|String}  options.formSubmitElement         The form submit element. Defaults to '[type="submit"]'.
	 * @param  {String}              options.formSubmitText            The form submission element text while shipping rates are being calculated. Defaults to 'Calculating...'.
	 * @param  {String}              options.recalcTriggerClass        The Recalculate button class name. Defaults to 'js-shipping-estimate-recalculate'.
	 * @param  {HTMLElement|String}  options.resultsTemplateContainer  The shipping rates template container selector. Defaults to '.js-shipping-estimate-results'.
	 * @param  {String}              options.sertApiFieldName          The SERT API field name. This field name is used on the SERT page to enable the API and return a JSON response. Defaults to 'sert_api'.
	 * @param  {HTMLElement|String}  options.shipStateElem             The ship state element. Defaults to '[name="ShipEstimate:ship_state"]'.
	 * @param  {HTMLElement|String}  options.shipStateSelectElem       The ship state select element. Defaults to '[name="ShipEstimate:ship_state_select"]'.
	 */
	constructor (formElement, {
		formFieldsContainer = '.js-shipping-estimate-fields',
		formFieldsControlClass = 'u-hidden',
		formSubmitElement = '[type="submit"]',
		formSubmitText = 'Calculating...',
		recalcTriggerClass = 'js-shipping-estimate-recalculate',
		resultsTemplateContainer = '.js-shipping-estimate-results',
		sertApiFieldName = 'sert_api',
		shipStateElem = '[name="ShipEstimate:ship_state"]',
		shipStateSelectElem = '[name="ShipEstimate:ship_state_select"]'
	} = {}) {
		this.#validator = new ParameterValidator(this);

		this.formElement = this.#validator.isElement('formElement', formElement);

		// Shipping estimate form object
		this.form = {
			fields: {
				container: this.#validator.isElement('formFieldsContainer', formFieldsContainer, {container: this.formElement}),
				controlClass: this.#validator.isString('formFieldsControlClass', formFieldsControlClass, {container: this.formElement}),
				shipStateElem: this.#validator.isElement('shipStateElem', shipStateElem, {
					container: this.formElement,
					required: false
				}),
				shipStateSelectElem: this.#validator.isElement('shipStateSelectElem', shipStateSelectElem, {
					container: this.formElement,
					required: false
				})
			},
			submit: {
				element: this.#validator.isElement('formSubmitElement', formSubmitElement, {container: this.formElement}),
				text: this.#validator.isString('formSubmitText', formSubmitText)
			}
		};
		this.form.submit.originalText = this.getFormSubmitText();

		// Results object
		this.results = {
			container: this.#validator.isElement('resultsTemplateContainer', resultsTemplateContainer, {container: this.formElement}),
			data: {
				rates: {},
				template: ''
			},
			recalculate: {
				triggerClass: this.#validator.isString('recalcTriggerClass', recalcTriggerClass)
			}
		};

		// API object
		this.api = {
			fieldName: this.#validator.isString('sertApiFieldName', sertApiFieldName),
			url: this.formElement.action
		};

		if (!this.api.url) {
			throw new Error(`${this.constructor.name} :: Invalid form action provided`);
		}

		// Bind base event listeners
		this.bindEvents();
	}

	/**
	 * Bind the base events
	 */
	bindEvents () {
		this.formElement.addEventListener('submit', this.onSubmit.bind(this), false);

		if (this.form.fields.shipStateSelectElem && this.form.fields.shipStateElem) {
			this.form.fields.shipStateSelectElem.addEventListener('change', () => this.shipStateSelectChange(), false);
			this.shipStateSelectChange();
		}
	}

	/**
	 * Bind the Recalculate events. This will typically be issued after the SERT
	 * template is retrieved because the template contains the Recalculate button.
	 */
	bindRecalculateEvents () {
		// Recalculate shipping button clicked, reset results HTML and show form fields
		this.results.recalculate.element.addEventListener('click', () => {
			this.hideTemplate();
			this.showFormFields();
		});
	}

	/**
	 * On change of Ship State Select, show/hide Ship State when applicable
	 */
	shipStateSelectChange () {
		if (this.form.fields.shipStateSelectElem.value) {
			this.form.fields.shipStateElem.disabled = true;

			if (this.form.fields.shipStateElem.parentNode) {
				this.form.fields.shipStateElem.parentNode.classList.add(this.form.fields.controlClass);
			}
		} else {
			this.form.fields.shipStateElem.disabled = false;

			if (this.form.fields.shipStateElem.parentNode) {
				this.form.fields.shipStateElem.parentNode.classList.remove(this.form.fields.controlClass);
			}
		}
	}

	/**
	 * Form submit handler
	 *
	 * @param	Event	event
	 */
	async onSubmit (event) {
		event.preventDefault();

		// Disable the form submission while the rates are calculating
		this.disableSubmitElement();

		// Get the shipping rates
		this.results.data = await this.getShippingRates();

		// Hide the shipping estimate form fields
		this.hideFormFields();

		// Display shipping estimate rates template
		this.showTemplate();

		// Set the Recalculate Shipping element now that the shipping rates template is loaded
		this.results.recalculate.element = this.#validator.isElement('onSubmit.results.recalculate.element', `.${this.results.recalculate.triggerClass}`, {container: this.formElement});

		if (this.results.recalculate.element) {
			this.bindRecalculateEvents();
		}

		// Enable the form submission element
		this.enableSubmitElement();
	}

	/**
	 * Get the shipping rates data
	 *
	 * @return	Object	Shipping rates response object. The object contains the following properties:
	 * 						- success: Flag to determine if the request was success/unsuccessful
	 * 						- rates: Object containing the shipping rates data
	 * 						- template: Rendered MVT SERT template
	 */
	async getShippingRates () {
		// Collect the estimated shipping form data
		const estShipFormData = new FormData(this.formElement);

		// Append the sertApiFieldName field if it does not exist
		if (!estShipFormData.hasOwnProperty(this.api.fieldName)) {
			estShipFormData.append(this.api.fieldName, 1);
		}

		// Send the estimated shipping rates request and return the JSON response
		const response = await fetch(this.api.url, {
			body: estShipFormData,
			method: 'POST'
		});

		if (!response.ok) {
			throw new Error('Network response was not successful');
		}

		return response.json();
	}

	/**
	 * Get the form submission element display text based on node name
	 *
	 * @return	String
	 */
	getFormSubmitText () {
		return this.isInputElement(this.form.submit.element) ? this.form.submit.element.value : this.form.submit.element.text;
	}

	/**
	 * Set the form submission element display text based on node name
	 *
	 * @param	String	text	The text value
	 */
	setFormSubmitText (text) {
		const submitText = this.#validator.isString('text', text);

		if (this.isInputElement(this.form.submit.element)) {
			this.form.submit.element.value = submitText;
		} else {
			this.form.submit.element.text = submitText;
		}
	}

	/**
	 * Determine if Element is an input Element
	 *
	 * @param	Element	element
	 *
	 * @return	Boolean
	 */
	isInputElement (element) {
		const testElm = this.#validator.isElement('element', element);

		return testElm.nodeName === 'INPUT';
	}

	/**
	 * Disable the form submit element
	 */
	disableSubmitElement () {
		this.form.submit.element.disabled = true;
		this.setFormSubmitText(this.form.submit.text);
	}

	/**
	 * Enable the form submit element
	 */
	enableSubmitElement () {
		this.setFormSubmitText(this.form.submit.originalText);
		this.form.submit.element.disabled = false;
	}

	/**
	 * Hide the form fields
	 */
	hideFormFields () {
		this.form.fields.container.classList.add(this.form.fields.controlClass);
	}

	/**
	 * Show the form fields
	 */
	showFormFields () {
		this.form.fields.container.classList.remove(this.form.fields.controlClass);
	}

	/**
	 * Hide the shipping rates template
	 */
	hideTemplate () {
		this.results.container.innerHTML = '';
	}

	/**
	 * Show the shipping rates template
	 */
	showTemplate () {
		this.results.container.innerHTML = this.results.data.template;
	}
}
